import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import {
  Sparkles, Wand2, Eye, Download, Trash2,
  Shield, ShieldCheck, Scale, Zap, Target, Flame,
  AlertTriangle, Edit3, HelpCircle,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Dialog, DialogContent, DialogTitle, DialogActions,
  Snackbar, Alert, TextField, Button, CircularProgress, LinearProgress,
  Tooltip as MuiTooltip,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "AuthContext";

import RadarChart6 from "./components/RadarChart6";
import { getAbilityBasis } from "./components/AbilityBasisInfo";
import StrategyDetailModal from "./components/StrategyDetailModal";
import FullKpiPanel, { labelForWarning } from "./components/FullKpiPanel";
import QuotaCard from "./components/QuotaCard";
import { TYPO, COLORS, RADIUS, CHART } from "./typography";
import {
  AMBITION_TO_RISK_TIER, RISK_TIER_TO_AMBITION,
  getSchool, getMove, getStrategyType, STRATEGY_TYPES, eliteToRadar, RADAR_AXES,
} from "./utils/genieMapping";
import {
  fetchMyStrategies, updateMyStrategy, deleteMyStrategy,
  getDownloadStatus, downloadStrategyExe, markGenieGuideSeen,
} from "./utils/genieApi";
import { LanguageContext } from "context/LanguageContext";
import UpgradePlanDialog from "components/mas/UpgradePlanDialog";
import { trackEvent } from "utils/analytics";

// 簡易插值：fillTemplate('剩餘 {n} 次', { n: 3 }) → '剩餘 3 次'
const fillTemplate = (str, vars = {}) =>
  String(str ?? '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : ''));

// 網頁端僅作策略管理 + 下載入口；MT5 部署參數（手數 / 本金 / MT5 帳號 / 狀態切換）
// 都在 EXE 端配置與運行，網頁不暴露這些欄位。

const SCHOOL_ICON_MAP = { Shield, Scale, Zap };
const MOVE_ICON_MAP = { ShieldCheck, Target, Flame };

// 舊版「自動預設名」= symbol + 中文策略類型名(存檔時寫死)；用來判斷 nickname 是不是「真・自訂」
// 是舊自動名 → 讓位給 AI 名;真的用戶改過的名 → 保留
const TYPE_NAME_SET = new Set(STRATEGY_TYPES.flatMap((t) => [t.name, t.type]));
const isAutoDefaultNickname = (nickname, symbol) => {
  if (!nickname || !symbol) return false;
  const n = String(nickname).trim();
  return [...TYPE_NAME_SET].some((tn) => n === `${symbol} ${tn}` || n === `${symbol}${tn}`);
};

// 從 elite.riskTier + monthlyReturn 推斷 (school, move)
// 規則跟 Revelation 一致：riskTier 直接對 school，move 暫時用 monthlyReturn 三段
// 因為這頁是「已選的」單一策略，沒法跨池切分，所以用 KPI heuristic：
//   monthlyReturn < 4%  → 守
//   monthlyReturn < 10% → 衡
//   其他               → 銳
function deriveSchoolMove(elite) {
  const ambitionKey = RISK_TIER_TO_AMBITION[elite?.riskTier] || 'balanced';
  const mr = Number(elite?.monthlyReturn || 0);
  let moveKey;
  if (mr < 4) moveKey = 'shou';
  else if (mr < 10) moveKey = 'heng';
  else moveKey = 'rui';
  return { ambitionKey, moveKey };
}

export default function MyGenieStrategies() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── i18n：載入「我的精靈策略」語言包（四語）
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("MyGenieStrategies", "mas-genie/MyGenieStrategies/MyGenieStrategies_lang");
  }, [language]);
  const L = useMemo(() => componentLanguageData.MyGenieStrategies?.Main || {}, [componentLanguageData]);
  const tfLabelL = (tf) => {
    if (!tf) return '—';
    return (L.tf && L.tf[tf]) || (L.tf?.fallback ? fillTemplate(L.tf.fallback, { tf }) : `${tf} K 線`);
  };
  const radarAxes = useMemo(() => RADAR_AXES.map((a) => ({ key: a.key, label: L.radar?.[a.key] || a.label })), [L]);
  // per-axis 能力依據（傳給雷達：每軸旁一顆 ⓘ → 點跳該軸自己的 tooltip）
  const _ab = getAbilityBasis(language);
  const abAxisInfo = Object.fromEntries((_ab.items || []).map((it) => [it.key, { param: it.param, desc: it.desc }]));
  const abBasedOn = _ab.basedOn;
  const stypeName = (st) => (st && L.stype?.[`${st.key}_name`]) || st?.name || '';
  // AI 名稱（依 UI 語言挑欄位；缺值回 null）— 菁英 AI 命名
  const eliteAiName = (elite) => {
    const e = elite || {};
    if (language === 'zh-CN') return e.nameZhCn || null;
    if ((language || '').startsWith('en')) return e.nameEn || null;
    return e.nameZhTw || null; // zh-TW 預設
  };
  // 策略顯示名：① 使用者自訂 nickname 最優先 → ② AI 多語名稱 → ③ 退回 symbol + 在地化類型名
  const displayStrategyName = (s, st, elite) => {
    const el = elite || s?.elite || {};
    const symbol = el.symbol || '';
    const defaultZh = `${symbol} ${st?.name || ''}`.trim();
    const ai = eliteAiName(el);
    // nickname 只有在「真・自訂」(非舊自動預設名) 時才最優先；舊自動名讓位給 AI 名
    if (s?.nickname && s.nickname !== defaultZh && !isAutoDefaultNickname(s.nickname, symbol)) {
      return s.nickname;
    }
    if (ai) return ai;
    return `${symbol} ${stypeName(st)}`.trim();
  };

  // 防 async fetch 在 unmount 後 setState
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  // 401 攔截改在 App.js 全域註冊一次

  const [strategies, setStrategies] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);  // { code, name, wizardSaveLimit, ... }
  const [quota, setQuota] = useState(null);        // { used, limit, remaining, canSubscribeMore }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editNickname, setEditNickname] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'info' });
  const [downloadingId, setDownloadingId] = useState(null);
  // 下載進度：null = 尚未開始接收（伺服器準備中，顯示不定量）；0–100 = 實際下載百分比
  const [downloadPct, setDownloadPct] = useState(null);
  // 新手導引：首次儲存策略後（從許願頁帶 justSubscribed 旗標進來）彈一次「如何開始交易」
  //   guideSeen 來自後端（跨裝置只看一次）；預設 true → 資料載入前不閃
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideSeen, setGuideSeen] = useState(true);
  const [spotlightOn, setSpotlightOn] = useState(false); // 我知道了後高亮第一張卡的「下載策略」
  const [spotRect, setSpotRect] = useState(null);        // 下載按鈕在視窗的座標（聚光燈環/氣泡定位）
  const guideFiredRef = useRef(false);
  const firstDownloadRef = useRef(null); // 第一張卡的下載按鈕（聚光燈定位用）
  const [detailKpiTab, setDetailKpiTab] = useState('summary'); // 檢視詳情卡分頁：'summary' 重點摘要 | 'advanced' 進階指標

  // ── 新手導引：首次從許願頁儲存策略後（justSubscribed）且後端標記未看過 → 自動彈一次
  //    「看過」記在後端（跨裝置只看一次）；開啟當下就 mark，確保只觸發一次
  //    ⚠️ 這些 effect 的依賴陣列在 render 當下就求值，必須放在上面 useState/useRef 宣告之後（否則 TDZ）
  const markGuideSeenOnce = () => {
    setGuideSeen(true);
    if (token) markGenieGuideSeen(token).catch((e) => console.warn('markGenieGuideSeen failed', e?.message));
  };
  useEffect(() => {
    if (guideFiredRef.current) return;
    if (location.state?.justSubscribed && !guideSeen && strategies.length > 0) {
      guideFiredRef.current = true;
      setGuideOpen(true);
      markGuideSeenOnce();
    }
  }, [location.state, guideSeen, strategies.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // 我知道了 → 關 modal，接著高亮第一張卡的「下載策略」（聚光燈）
  const finishGuideModal = () => {
    setGuideOpen(false);
    if (firstDownloadRef.current) setSpotlightOn(true);
  };
  const closeGuide = () => setGuideOpen(false);            // X：只關，不彈聚光燈
  const closeSpotlight = () => setSpotlightOn(false);
  const reopenGuide = () => { setSpotlightOn(false); setGuideOpen(true); }; // 使用教學手動重看

  // 聚光燈開啟時：把第一張卡的下載按鈕捲到可視，並算出座標給環/氣泡定位；scroll/resize 跟著更新
  useEffect(() => {
    if (!spotlightOn) { setSpotRect(null); return; }
    const el = firstDownloadRef.current;
    if (!el) { setSpotlightOn(false); return; }
    try { el.scrollIntoView({ block: 'center', inline: 'nearest' }); } catch (_) {}
    const compute = () => {
      const r = el.getBoundingClientRect();
      setSpotRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [spotlightOn]);

  const showToast = (msg, severity = 'info') => setToast({ open: true, msg, severity });

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMyStrategies(token);
      if (!isMountedRef.current) return;
      setStrategies(Array.isArray(data?.items) ? data.items : []);
      setPlanInfo(data?.plan || null);
      setQuota(data?.quota || null);
      setGuideSeen(!!data?.genieGuideSeen);
    } catch (err) {
      console.error('fetchMyStrategies failed', err);
      if (isMountedRef.current) setError(err.message || L.list?.loadFail || '載入失敗');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  // 開啟編輯 dialog（網頁端僅允許改名稱）
  const openEdit = (s) => {
    setEditing(s);
    setEditNickname(s.nickname || '');
  };

  const closeEdit = () => { setEditing(null); setEditNickname(''); };

  const handleSaveEdit = async () => {
    if (!editing || savingEdit) return;
    const trimmed = (editNickname || '').trim();
    setSavingEdit(true);
    try {
      await updateMyStrategy(token, editing.id, { nickname: trimmed || null });
      showToast(L.toast?.nameUpdated || '名稱已更新', 'success');
      closeEdit();
      refresh();
    } catch (err) {
      showToast(fillTemplate(L.toast?.updateFail || '更新失敗：{msg}', { msg: err.message || L.toast?.tryLater || '請稍後再試' }), 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  // 刪除確認用 MUI Dialog 取代 window.confirm（PO 截圖五 spec）
  const [deletingTarget, setDeletingTarget] = useState(null); // { id, name } | null
  // 下載前風險提醒 modal（PO 截圖三 spec）
  const [downloadModalTarget, setDownloadModalTarget] = useState(null); // strategy obj | null
  // 2026-06-14 FREE 下載卡控：planBlocked → 點下載直接跳升級視窗（不開風險提醒 modal）
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const handleDelete = (s) => {
    if (deletingId) return;
    const name = s.nickname || fillTemplate(L.misc?.nameFallback || '{symbol} 策略', { symbol: s.elite?.symbol || '?' });
    setDeletingTarget({ id: s.id, name });
  };
  const confirmDelete = async () => {
    if (!deletingTarget) return;
    const { id } = deletingTarget;
    setDeletingId(id);
    setDeletingTarget(null);
    try {
      await deleteMyStrategy(token, id);
      showToast(L.toast?.removed || '已移除', 'success');
      refresh();
    } catch (err) {
      showToast(fillTemplate(L.toast?.removeFail || '移除失敗：{msg}', { msg: err.message || L.toast?.tryLater || '請稍後再試' }), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  // 階段 3：下載執行檔（預檢 + blob 下載）
  const handleDownload = async (s) => {
    setDownloadingId(s.id);
    setDownloadPct(null);
    try {
      const status = await getDownloadStatus(token, s.id);
      if (!status.canDownload) {
        const errReason = (status.reasons || []).find((r) => r.severity === 'error')
          || (status.reasons || [])[0];
        const code = errReason?.code;
        const msg = errReason?.msg || L.toast?.cantDownload || '無法下載';
        showToast(msg, code === 'plan-insufficient' ? 'warning' : 'error');
        if (code === 'plan-insufficient') {
          setUpgradeOpen(true);
        } else if (code === 'elite-archived') {
          setTimeout(() => navigate('/mas-genie'), 1500);
        } else if (code === 'strategy-removed') {
          // 訂閱已被軟刪（可能在其他 tab 刪掉的） — 刷新列表
          refresh();
        }
        return;
      }
      // 檔名用卡片同一套 displayStrategyName（會跳過舊自動預設暱稱、優先 AI 名）→ 保證卡片 = 下載一致
      const _dlElite = s.elite || {};
      const { ambitionKey: _dlAk, moveKey: _dlMk } = deriveSchoolMove(_dlElite);
      const _dlName = (displayStrategyName(s, getStrategyType(_dlAk, _dlMk), _dlElite) || _dlElite.symbol || 'strategy')
        .replace(/[<>:"/\\|?*\x00-\x1f]/g, "").replace(/\s+/g, " ").trim().slice(0, 80);
      await downloadStrategyExe(
        token, s.id,
        `${_dlName}.exe`,
        ({ percent }) => setDownloadPct(percent),
      );
      trackEvent("download_exe", { strategy_id: s.id, symbol: s.elite?.symbol });
      showToast(L.toast?.startDl || '開始下載', 'success');
    } catch (err) {
      console.error('download failed', err);
      const code = err.code;
      if (code === 'plan-insufficient') {
        showToast(L.toast?.planNoDl || '方案不支援下載，將引導您升級', 'warning');
        setUpgradeOpen(true);
      } else if (code === 'elite-archived') {
        showToast(L.toast?.archived || '策略已下架，請至 MAS 精靈重選', 'error');
        setTimeout(() => navigate('/mas-genie'), 1500);
      } else if (code === 'strategy-removed') {
        showToast(L.toast?.subRemoved || '此訂閱已被移除，列表已刷新', 'error');
        refresh();
      } else if (code === 'exe-not-ready' || code === 'exe-file-missing') {
        showToast(L.toast?.exeNotReady || '執行檔尚未準備好，請稍後再試', 'warning');
      } else {
        showToast(fillTemplate(L.toast?.dlFail || '下載失敗：{msg}', { msg: err.message || L.toast?.tryLater || '請稍後再試' }), 'error');
      }
    } finally {
      setDownloadingId(null);
      setDownloadPct(null);
    }
  };

  // 配額顯示 — 雙配額（訂閱 + 月許願）
  const used = quota?.used ?? strategies.length;
  const limit = quota?.limit;
  const planCode = planInfo?.code || 'FREE';
  const planName = planInfo?.name || planCode;
  // 2026-06-14：下載/匯出 EXE 的方案閘門 = wizardExecLimit===0（FREE）。與後端 download 閘門同規則；
  //   STARTER(試用)/GROWTH/PRO 皆可下載。planInfo 未載入時不擋（後端仍 enforce）。
  const planBlocked = !!planInfo && planInfo.wizardExecLimit === 0;
  const saveText = limit == null
    ? `訂閱 ${used} 個`
    : limit === -1
      ? `訂閱 ${used}（∞）`
      : `訂閱 ${used} / ${limit}`;
  const isSaveAtLimit = quota && quota.canSubscribeMore === false;

  const monthlyUsed = quota?.monthlyUsed;
  const monthlyLimit = quota?.monthlyLimit;
  const monthlyResetAt = quota?.monthlyResetAt;
  const showMonthly = monthlyLimit != null;
  const monthlyText = !showMonthly
    ? null
    : monthlyLimit === -1
      ? `本月許願 ${monthlyUsed || 0}（∞）`
      : `本月許願 ${monthlyUsed || 0} / ${monthlyLimit}`;
  const isMonthlyAtLimit = showMonthly && monthlyLimit !== -1 && (monthlyUsed || 0) >= monthlyLimit;

  // 距離下次月配額重置剩幾天
  let resetCountdown = null;
  if (monthlyResetAt) {
    const days = Math.max(0, Math.ceil((new Date(monthlyResetAt).getTime() - Date.now()) / 86400000));
    resetCountdown = days === 0 ? '今日重置' : `${days} 天後重置`;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={4} px={3}>
        {/* ───────────────────────── 頂部配額 cards — 跟 intro/ambition 一致 */}
        {(quota && planInfo) && (
          <div className="grid grid-cols-2 gap-4 md:gap-5 mb-6 mt-2">
            <QuotaCard
              icon="/images/icon/genie/download.png"
              label={L.quota?.save || '策略儲存'}
              remainingLabel={L.quota?.remaining || '剩餘'}
              remaining={quota?.remaining ?? '—'}
              limit={limit}
              tooltip={limit === -1 ? (L.quota?.saveUnlimited || '您的方案可保存策略無上限') : fillTemplate(L.quota?.saveRemain || '還可保存 {n} 個策略', { n: quota?.remaining ?? 0 })}
            />
            <QuotaCard
              icon="/images/icon/genie/genie.png"
              label={L.quota?.genie || '策略精靈'}
              remainingLabel={L.quota?.remaining || '剩餘'}
              remaining={quota?.monthlyRemaining ?? '—'}
              limit={monthlyLimit}
              tooltip={monthlyLimit === -1 ? (L.quota?.genieUnlimited || '您的方案本月精靈使用次數無上限') : fillTemplate(L.quota?.genieRemain || '本月還可使用 {n} 次策略精靈', { n: quota?.monthlyRemaining ?? 0 })}
            />
          </div>
        )}

        {/* ───────────────────────── Hero block 包整個策略列表 */}
        <div className="bg-white rounded-[32px] p-6 md:p-10 border border-slate-100 shadow-md">
          <div
            className="flex justify-between items-center flex-wrap gap-3"
            style={{ borderBottom: '1px solid #EFEFEF', paddingBottom: 20, marginBottom: 24 }}
          >
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-black text-slate-900 tracking-tight flex items-center gap-2.5">
                <span style={{ display: 'inline-block', width: 4, height: 22, background: '#fb923c', borderRadius: 2 }} />
                <span className="text-2xl md:text-3xl">{L.header?.myStrategies || '我的策略'}</span>
              </h2>
              {/* 使用教學 — 隨時可重看「如何開始交易」導引 */}
              <button
                type="button"
                onClick={reopenGuide}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  color: COLORS.main, ...TYPO.bodyM, fontWeight: 500,
                  textDecorationLine: 'underline', textUnderlineOffset: 'auto',
                }}
              >
                <HelpCircle size={16} /> {L.guide?.help || '使用教學'}
              </button>
            </div>
            <Link
              to="/mas-genie?force=true"
              style={{
                ...TYPO.title, lineHeight: 'normal',
                display: 'flex', height: 35,
                padding: '8px 16px 8px 8px',
                justifyContent: 'center', alignItems: 'center', gap: 4,
                borderRadius: 8, border: '1px solid #F36D26', background: COLORS.white,
                color: COLORS.main,
                textDecoration: 'none',
              }}
            >
              <img
                src="/images/icon/genie/plus.png"
                alt=""
                style={{ width: 20, height: 20, flexShrink: 0, aspectRatio: '1 / 1', objectFit: 'contain' }}
              />
              {L.header?.addStrategy || '新增策略'}
            </Link>
          </div>

          {/* loading / error / empty */}
          {loading && (
            <MDBox textAlign="center" py={6}>
              <CircularProgress size={32} />
              <MDTypography variant="body2" color="text" mt={2}>{L.list?.loading || '載入您的策略中...'}</MDTypography>
            </MDBox>
          )}
          {!loading && error && (
            <div className="text-center py-6 px-4 rounded-2xl border border-dashed border-rose-200 bg-rose-50">
              <p className="text-base font-black text-rose-700 mb-1">{L.list?.loadFail || '載入失敗'}</p>
              <p className="text-sm text-rose-600 mb-3">{error}</p>
              <Button onClick={refresh}>{L.list?.retry || '重試'}</Button>
            </div>
          )}
          {!loading && !error && strategies.length === 0 && (
            <div className="text-center py-10 px-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50">
              <p className="text-base font-black text-slate-700 mb-1">{L.list?.emptyTitle || '還沒有任何精靈策略'}</p>
              <p className="text-sm text-slate-500">{L.list?.emptyDesc || '點右上方「+ 新增策略」開始召喚精靈'}</p>
            </div>
          )}

          {/* ───────────────────────── 卡片 grid（固定 3 欄 repeat(3,1fr)，響應式降欄）
              固定 3 欄 → 每欄永遠 = 容器 1/3：
                3 個 = 三欄滿版；2 個 = 左兩格；1 個 = 左一格（皆不被 1fr 撐大、靠左）
              md 以下降 2 欄、手機 1 欄，避免卡片過窄按鈕擠在一起 */}
          {!loading && !error && strategies.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3" style={{ gap: 40 }}>
              {strategies.map((s, idx) => {
                const elite = s.elite || {};
                const { ambitionKey, moveKey } = deriveSchoolMove(elite);
                const strategyType = getStrategyType(ambitionKey, moveKey);
                const radar = eliteToRadar(elite, moveKey);
                // 三色 banner — 跟 Revelation 卡一致
                const BANNER = {
                  shou: { label: L.card?.bannerShou || '保守', color: COLORS.riskLow },
                  heng: { label: L.card?.bannerHeng || '平衡', color: COLORS.riskMid },
                  rui:  { label: L.card?.bannerRui || '積極', color: COLORS.riskHigh },
                };
                const banner = BANNER[moveKey] || BANNER.heng;
                // 健康指數箭頭（從後端 healthLevel HIGH/MEDIUM/LOW 推）
                const healthMap = {
                  HIGH:    { label: L.health?.high || '高', arrow: '↑', color: '#16a34a' },
                  MEDIUM:  { label: L.health?.mid || '中', arrow: '–', color: '#f59e0b' },
                  LOW:     { label: L.health?.low || '低', arrow: '↓', color: '#dc2626' },
                  UNKNOWN: { label: L.health?.na || '—', arrow: '',    color: '#94a3b8' },
                };
                const health = healthMap[s.healthLevel] || healthMap.UNKNOWN;
                // 下載按鈕細分文字
                const isDownloading = downloadingId === s.id;
                let dlText = L.dl?.download || '下載策略';
                if (isDownloading) dlText = L.dl?.downloading || '下載中';
                else if (!s.canDownloadNew) {
                  if (s.eliteArchived === true) dlText = L.dl?.delisted || '已下架';
                  else if (s.eliteExeReady === false && s.eliteLiveReady === true) dlText = L.dl?.packaging || '打包中';
                  else dlText = L.dl?.cant || '無法下載';
                }
                const canDownload = s.canDownloadNew && !isDownloading;

                return (
                  <div
                    key={s.id}
                    className="bg-white overflow-hidden flex flex-col items-center"
                    style={{
                      width: '100%',
                      borderRadius: 12,
                      border: '1px solid #E6E6E6',
                      background: COLORS.white,
                      boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.20)',
                    }}
                  >
                    {/* 策略名稱區塊 — padding 16/24, border-bottom #F6F6F6, align-self stretch */}
                    <div className="flex justify-between items-center gap-2" style={{ padding: '16px 24px', alignSelf: 'stretch', borderBottom: '1px solid #F6F6F6' }}>
                      <h3 className="flex items-center gap-2 min-w-0" style={{ ...TYPO.headingM, color: '#0A0A0A' }}>
                        <span style={{ display: 'inline-block', width: 4, height: 18, background: banner.color, borderRadius: 2, flexShrink: 0 }} />
                        <span className="truncate">{displayStrategyName(s, strategyType, elite)}</span>
                      </h3>
                      <div className="flex gap-1.5 shrink-0">
                        {/* symbol — 淡橘底 filled */}
                        <span style={{ ...TYPO.numS, padding: '4px 16px', borderRadius: 2, background: COLORS.lighter, color: COLORS.main }}>
                          {elite.symbol || ''}
                        </span>
                        {/* timeframe — 橘框白底 outlined */}
                        <span style={{ ...TYPO.numS, padding: '4px 16px', borderRadius: 2, border: '1px solid #F36D26', background: COLORS.white, color: COLORS.main }}>
                          {elite.timeframe || ''}
                        </span>
                      </div>
                    </div>

                    {/* 中間區塊 — banner + 雷達 + KPI + 健康度；padding 24px 0, gap 8, align-self stretch */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '24px 0', alignSelf: 'stretch' }}>
                      {/* banner — align-self stretch + 左右內縮 32px；radius 2 / 16px-500 白字 */}
                      <div
                        style={{ ...TYPO.title, lineHeight: 'normal', alignSelf: 'stretch', margin: '0 32px', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5px 0 6px 0', borderRadius: 2, background: banner.color, color: COLORS.white, textAlign: 'center' }}
                      >
                        {banner.label}
                      </div>

                    {/* radar — 寬隨卡片、高用 aspect-ratio 等比縮放（長也跟著卡片寬縮放）*/}
                    <div style={{ width: '100%', aspectRatio: '360 / 230' }}>
                      <RadarChart6
                        values={radar}
                        color={banner.color}
                        variant="full"
                        height="100%"
                        instanceKey={`my-card-${s.id}`}
                        labelFontSize={CHART.radarLabelSize}
                        dotRadius={CHART.radarDotRadius}
                        dotFontSize={CHART.radarValueSize}
                        outerRadius="90%"
                        axes={radarAxes}
                        axisInfo={abAxisInfo}
                        basedOn={abBasedOn}
                      />
                    </div>

                    {/* 3 KPI — 靠近置中（不分太開），數字 24px/500 橘 */}
                    <div className="flex justify-center gap-8 mb-3 px-4 text-center">
                      <div>
                        <p className="text-xs font-bold text-slate-600 font-sans mb-0.5">{L.card?.kpiMonthly || '月報酬'}</p>
                        <p style={{ ...TYPO.numKpi, color: elite.monthlyReturn != null ? COLORS.main : '#94a3b8' }}>
                          {elite.monthlyReturn != null ? `${Number(elite.monthlyReturn).toFixed(1)}%` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-600 font-sans mb-0.5">{L.card?.kpiWin || '勝率'}</p>
                        <p style={{ ...TYPO.numKpi, color: elite.winRate != null ? COLORS.main : '#94a3b8' }}>
                          {elite.winRate != null ? `${Number(elite.winRate).toFixed(0)}%` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-600 font-sans mb-0.5">{L.card?.kpiMdd || 'MDD'}</p>
                        <p style={{ ...TYPO.numKpi, color: elite.maxDrawdown != null ? COLORS.main : '#94a3b8' }}>
                          {elite.maxDrawdown != null ? `${Number(elite.maxDrawdown).toFixed(1)}%` : '—'}
                        </p>
                      </div>
                    </div>

                    {/* 平均多久一筆 —— 日線策略隨機某天開跑，30 天內有 1/3 機率
                        連一次開倉都沒有。原本這個數字只在詳細視窗裡。 */}
                    {(elite.tradePacePerDay != null || elite.tradePaceDays != null) && (
                      <p className="text-center text-xs text-slate-500 font-sans mb-3">
                        {elite.tradePacePerDay != null
                          ? (L.card?.tradePaceDaily || '平均一天約 {n} 筆').replace('{n}', elite.tradePacePerDay)
                          : (L.card?.tradePace || '平均約 {n} 天一筆').replace('{n}', elite.tradePaceDays)}
                      </p>
                    )}

                    {/* 短分隔線：KPI ↔ 健康指數（PO 指定）*/}
                    <div style={{ width: 48, height: 1, background: COLORS.grayLighter, margin: '0 auto 12px' }} />

                    {/* 策略健康指數 — icon 灰(#8A8A8A)；「策略健康指數」灰字、高/中/低 橘字；副字淺灰 */}
                    <div className="text-center pb-3 px-4">
                      <p className="flex items-center justify-center gap-1.5" style={{ fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif' }}>
                        <img src="/images/icon/genie/healthy.png" alt="" style={{ width: 13.333, height: 16.667, objectFit: 'contain', filter: 'brightness(0) saturate(100%) invert(58%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(95%) contrast(85%)' }} />
                        <span style={{ ...TYPO.title, color: COLORS.grayDark }}>{L.card?.healthLabel || '策略健康指數'}</span>
                        <span style={{ ...TYPO.title, color: COLORS.main }}>
                          {health.label} {health.arrow}
                        </span>
                      </p>
                      <p style={{ ...TYPO.bodyS, color: COLORS.grayMedian, marginTop: 2 }}>{L.card?.healthNote || '健康指數會定期檢測'}</p>
                      </div>
                    </div>{/* 中間區塊 end */}

                    {/* 下面按鈕區塊 — padding 24, border-top #F6F6F6, align-self stretch */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 24, alignSelf: 'stretch', borderTop: '1px solid #F6F6F6' }}>
                    {/* 4 按鈕單行（不換行）：刪除/編輯 內容寬、查看詳情/下載 flex-1 平分 */}
                    <div className="flex gap-1.5" style={{ width: '100%', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif' }}>
                      {/* 刪除 — icon 按鈕（del.png 已含紅框）*/}
                      <button
                        onClick={() => handleDelete(s)}
                        disabled={deletingId === s.id}
                        className="disabled:opacity-50"
                        title={L.btn?.del || '刪除'}
                        style={{
                          width: 30, height: 30, padding: 0, boxSizing: 'border-box',
                          display: 'flex', justifyContent: 'center', alignItems: 'center',
                          border: 'none', background: 'none',
                          flexShrink: 0, cursor: 'pointer',
                        }}
                      >
                        <img src="/images/icon/genie/del.png" alt={L.btn?.del || '刪除'} style={{ width: 30, height: 30, objectFit: 'contain' }} />
                      </button>
                      {/* 編輯 — icon 按鈕（edit.png 已含黑框）*/}
                      <button
                        onClick={() => openEdit(s)}
                        title={L.btn?.edit || '編輯'}
                        style={{
                          width: 30, height: 30, padding: 0, boxSizing: 'border-box',
                          display: 'flex', justifyContent: 'center', alignItems: 'center',
                          border: 'none', background: 'none',
                          flexShrink: 0, cursor: 'pointer',
                        }}
                      >
                        <img src="/images/icon/genie/edit.png" alt={L.btn?.edit || '編輯'} style={{ width: 30, height: 30, objectFit: 'contain' }} />
                      </button>
                      {/* 查看詳情 — 黑框 + eye-open icon，flex-1 */}
                      <button
                        onClick={() => setViewing(s)}
                        style={{
                          height: 30, padding: '0 6px', boxSizing: 'border-box',
                          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3,
                          borderRadius: 8, border: '1px solid #000', background: COLORS.white,
                          color: COLORS.black, ...TYPO.bodyS,
                          flex: '1 1 0', minWidth: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        <img src="/images/icon/genie/eye-open.png" alt="" style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }} />
                        {L.btn?.view || '查看詳情'}
                      </button>
                      {/* 下載策略 — 橘框+橘底 #F36D26 白字 + Downlaod icon，flex-1.3（最寬）
                          第一張卡掛 ref → 新手導引聚光燈定位用 */}
                      <button
                        ref={idx === 0 ? firstDownloadRef : undefined}
                        onClick={() => { if (planBlocked) { setUpgradeOpen(true); return; } canDownload && setDownloadModalTarget(s); }}
                        disabled={!planBlocked && !canDownload}
                        style={{
                          height: 30, padding: '0 6px', boxSizing: 'border-box',
                          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3,
                          borderRadius: 8,
                          border: `1px solid ${(canDownload || planBlocked) ? COLORS.main : '#94a3b8'}`,
                          background: (canDownload || planBlocked) ? COLORS.main : '#94a3b8',
                          color: COLORS.white, ...TYPO.bodyS,
                          flex: '1.3 1 0', minWidth: 0, cursor: (canDownload || planBlocked) ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap',
                        }}
                      >
                        <img src="/images/icon/genie/Downlaod.png" alt="" style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0, filter: 'brightness(0) invert(1)' }} />
                        {planBlocked ? (L.dl?.upgrade || '升級下載') : dlText}
                      </button>
                    </div>
                    </div>{/* 按鈕區塊 end */}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </MDBox>

      {/* ───────────────────────── 查看詳情 modal（reuse Revelation ReportModal layout）
          底部按鈕改「返回方案列表 / 下載策略」 */}
      {viewing && (() => {
        const elite = viewing.elite || {};
        const { ambitionKey, moveKey } = deriveSchoolMove(elite);
        const strategyType = getStrategyType(ambitionKey, moveKey);
        const radar = eliteToRadar(elite, moveKey);
        const BANNER = {
          shou: { label: L.card?.bannerShou || '保守', color: COLORS.riskLow },
          heng: { label: L.card?.bannerHeng || '平衡', color: COLORS.riskMid },
          rui:  { label: L.card?.bannerRui || '積極', color: COLORS.riskHigh },
        };
        const banner = BANNER[moveKey] || BANNER.heng;
        const _healthStatusMap = {
          HIGH:    { label: L.health?.statusHigh || '✅ 健康', color: '#2e7d32' },
          MEDIUM:  { label: L.health?.statusMedium || '🟡 待轉換', color: '#f57c00' },
          LOW:     { label: L.health?.statusLow || '🔴 健康度低，請更換', color: '#c62828' },
          UNKNOWN: { label: L.health?.statusUnknown || '❓ 未知', color: '#757575' },
        };
        const _hs = _healthStatusMap[viewing.healthLevel] || { label: viewing.healthLabel || '—', color: viewing.healthColor || '#475569' };
        const isDownloading = downloadingId === viewing.id;
        let dlText = L.dl?.download || '下載策略';
        if (isDownloading) dlText = L.dl?.downloading || '下載中';
        else if (!viewing.canDownloadNew) {
          if (viewing.eliteArchived === true) dlText = L.dl?.delisted || '已下架';
          else if (viewing.eliteExeReady === false && viewing.eliteLiveReady === true) dlText = L.dl?.packaging || '打包中';
          else dlText = L.dl?.cant || '無法下載';
        }
        const canDownload = viewing.canDownloadNew && !isDownloading;
        return (
          <StrategyDetailModal
            open={!!viewing}
            onClose={() => setViewing(null)}
            L={L}
            title={displayStrategyName(viewing, strategyType, elite)}
            typeLabel={banner.label}
            typeColor={banner.color}
            elite={elite}
            radarValues={radar}
            radarColor={banner.color}
            radarKey={`my-detail-${viewing.id}`}
            radarAxes={radarAxes}
            healthDisplay={_hs.label}
            healthColor={_hs.color}
            tfLabel={tfLabelL}
            footer={(
              <>
                <button
                  onClick={() => setViewing(null)}
                  style={{
                    display: 'flex', height: 48, padding: '12px clamp(10px, 4vw, 32px)', boxSizing: 'border-box',
                    flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap',
                    justifyContent: 'center', alignItems: 'center', gap: 10,
                    borderRadius: 8, border: 'none', background: COLORS.grayLightest,
                    color: COLORS.black, textAlign: 'center', ...TYPO.title, cursor: 'pointer',
                  }}
                >
                  {L.btn?.backToList || '返回方案列表'}
                </button>
                <button
                  onClick={() => {
                    if (planBlocked) { setUpgradeOpen(true); setViewing(null); return; }
                    setDownloadModalTarget(viewing);
                    setViewing(null);
                  }}
                  disabled={!planBlocked && !canDownload}
                  style={{
                    display: 'flex', height: 48, padding: '12px clamp(10px, 4vw, 32px)', boxSizing: 'border-box',
                    flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap',
                    justifyContent: 'center', alignItems: 'center', gap: 10,
                    borderRadius: 8, border: 'none',
                    background: (canDownload || planBlocked) ? COLORS.main : '#94a3b8',
                    color: COLORS.white, textAlign: 'center', ...TYPO.title,
                    cursor: (canDownload || planBlocked) ? 'pointer' : 'not-allowed', opacity: (canDownload || planBlocked) ? 1 : 0.6,
                  }}
                >
                  {planBlocked ? (L.dl?.upgrade || '升級下載') : dlText}
                </button>
              </>
            )}
          />
        );
      })()}

      {/* ───────────────────────── 編輯名稱 dialog（PO 截圖四 spec）*/}
      <Dialog
        open={!!editing}
        onClose={closeEdit}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 90, p: 3, boxSizing: 'border-box', borderBottom: `1px solid ${COLORS.grayLighter}` }}>
          <span style={{ ...TYPO.modalTitle, color: COLORS.black }}>{L.edit?.title || '編輯'}</span>
          <button
            onClick={closeEdit}
            style={{ display: 'flex', width: 40, height: 40, padding: 8, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <span style={{ display: 'flex', width: 24, height: 24, padding: 2, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
              <img src="/images/icon/cross.png" alt={L.modal?.closeAlt || '關閉'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </span>
          </button>
        </DialogTitle>
        <DialogContent sx={{ p: '24px', pt: '24px !important', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', alignSelf: 'stretch' }}>
          <p style={{ ...TYPO.title, color: COLORS.black, margin: 0 }}>
            {L.edit?.nameLabel || '策略名稱'}<span style={{ color: COLORS.error, marginLeft: 2 }}>*</span>
          </p>
          <input
            type="text"
            autoFocus
            value={editNickname}
            onChange={(e) => setEditNickname(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); }}
            placeholder={L.edit?.placeholder || '例如：黃金中波段策略'}
            style={{
              ...TYPO.bodyM,
              width: '100%',
              padding: '12px 14px',
              borderRadius: RADIUS.m,
              border: `1.5px solid ${COLORS.grayLighter}`,
              background: '#f8fafc',
              color: '#0f172a',
              outline: 'none',
            }}
            onFocus={(e) => { e.target.style.borderColor = COLORS.main; e.target.style.background = COLORS.white; }}
            onBlur={(e) => { e.target.style.borderColor = COLORS.grayLighter; e.target.style.background = '#f8fafc'; }}
          />
        </DialogContent>
        <DialogActions sx={{ p: '24px 24px 0 24px', minHeight: 96, boxSizing: 'border-box', gap: 1.5, borderTop: `1px solid ${COLORS.grayLighter}`, background: COLORS.white, alignItems: 'flex-start' }}>
          <button
            onClick={closeEdit}
            disabled={savingEdit}
            style={{
              display: 'flex', height: 48, padding: '12px 32px', boxSizing: 'border-box',
              justifyContent: 'center', alignItems: 'center', gap: 10,
              borderRadius: 8, border: 'none', background: COLORS.grayLightest,
              color: COLORS.black, textAlign: 'center', ...TYPO.title,
              cursor: savingEdit ? 'not-allowed' : 'pointer', opacity: savingEdit ? 0.6 : 1,
            }}
          >
            {L.btn?.cancel || '取消'}
          </button>
          <button
            onClick={handleSaveEdit}
            disabled={savingEdit}
            style={{
              display: 'flex', height: 48, padding: '12px 32px', boxSizing: 'border-box',
              justifyContent: 'center', alignItems: 'center', gap: 10,
              borderRadius: 8, border: 'none', background: COLORS.main,
              color: COLORS.white, textAlign: 'center', ...TYPO.title,
              cursor: savingEdit ? 'not-allowed' : 'pointer', opacity: savingEdit ? 0.6 : 1,
            }}
          >
            {savingEdit ? (L.edit?.saving || '儲存中...') : (L.btn?.confirm || '確認')}
          </button>
        </DialogActions>
      </Dialog>

      {/* ───────────────────────── 刪除確認 dialog（PO 截圖五 spec）*/}
      <Dialog
        open={!!deletingTarget}
        onClose={() => setDeletingTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 90, p: 3, boxSizing: 'border-box', borderBottom: '1px solid #E6E6E6' }}>
          <span style={{ ...TYPO.modalTitle, color: COLORS.black }}>{L.del?.title || '刪除此策略'}</span>
          <button
            onClick={() => setDeletingTarget(null)}
            style={{ display: 'flex', width: 40, height: 40, padding: 8, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <span style={{ display: 'flex', width: 24, height: 24, padding: 2, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
              <img src="/images/icon/cross.png" alt={L.modal?.closeAlt || '關閉'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </span>
          </button>
        </DialogTitle>
        <DialogContent sx={{ p: '24px', pt: '24px !important', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '16px', alignSelf: 'stretch' }}>
          <p style={{ ...TYPO.title, color: COLORS.black, margin: 0 }}>
            {L.del?.confirmText || '確認刪除此策略'}
          </p>
          <p style={{ ...TYPO.bodyM, color: COLORS.red, margin: 0 }}>
            {L.del?.warnText || '此動作將無法復原，確定刪除？'}
          </p>
        </DialogContent>
        <DialogActions sx={{ p: '24px 24px 0 24px', minHeight: 96, boxSizing: 'border-box', gap: 1.5, borderTop: '1px solid #E6E6E6', background: COLORS.white, alignItems: 'flex-start' }}>
          <button
            onClick={() => setDeletingTarget(null)}
            style={{
              display: 'flex', height: 48, padding: '12px 32px', boxSizing: 'border-box',
              justifyContent: 'center', alignItems: 'center', gap: 10,
              borderRadius: 8, border: 'none', background: COLORS.grayLightest,
              color: COLORS.black, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
              ...TYPO.title, cursor: 'pointer',
            }}
          >
            {L.btn?.cancel || '取消'}
          </button>
          <button
            onClick={confirmDelete}
            style={{
              display: 'flex', height: 48, padding: '12px 32px', boxSizing: 'border-box',
              justifyContent: 'center', alignItems: 'center', gap: 10,
              borderRadius: 8, border: 'none', background: COLORS.red,
              color: COLORS.white, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
              ...TYPO.title, cursor: 'pointer',
            }}
          >
            {L.del?.confirmBtn || '確定刪除'}
          </button>
        </DialogActions>
      </Dialog>

      {/* ───────────────────────── 下載前風險提醒 dialog（PO 截圖三 spec）
          點「下載策略」會先彈這個說明，按「開始下載」才真的呼叫 handleDownload */}
      <Dialog
        open={!!downloadModalTarget}
        onClose={() => setDownloadModalTarget(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 90, p: 3, boxSizing: 'border-box', borderBottom: '1px solid #E6E6E6' }}>
          <span style={{ ...TYPO.modalTitle, color: COLORS.black }}>{L.dlModal?.title || '下載檔案'}</span>
          <button
            onClick={() => setDownloadModalTarget(null)}
            style={{ display: 'flex', width: 40, height: 40, padding: 8, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <span style={{ display: 'flex', width: 24, height: 24, padding: 2, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
              <img src="/images/icon/cross.png" alt={L.modal?.closeAlt || '關閉'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </span>
          </button>
        </DialogTitle>
        <DialogContent sx={{ p: '24px', pt: '24px !important', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '16px', alignSelf: 'stretch' }}>
          {/* 風險提醒 */}
          <div>
            <p style={{ ...TYPO.title, color: COLORS.black, marginBottom: 8 }}>{L.dlModal?.riskLabel || '風險提醒：'}</p>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, marginBottom: 4 }}>
              {L.dlModal?.risk1 || '本平台僅提供策略與分析工具，供使用者研究參考，並非投資建議、代客操作或收益保證。'}
            </p>
            <p style={{ ...TYPO.bodyM, color: COLORS.red }}>
              {L.dlModal?.risk2 || '使用者應自行評估策略風險與市場變化，並自行承擔交易損益。'}
            </p>
          </div>
          {/* 檔案提醒 */}
          <div>
            <p style={{ ...TYPO.title, color: COLORS.black, marginBottom: 8 }}>{L.dlModal?.fileLabel || '檔案提醒：'}</p>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, marginBottom: 4 }}>
              {L.dlModal?.file1 || '本下載檔案為 Windows 專用的 exe 執行檔，僅適用於 Windows 環境的電腦'}
            </p>
            <p style={{ ...TYPO.bodyM, color: COLORS.red }}>
              {L.dlModal?.file2 || '手機或平板及其他作業系統無法執行'}
            </p>
          </div>
          {/* 系統相容性說明 */}
          <div>
            <p style={{ ...TYPO.title, color: COLORS.black, marginBottom: 8 }}>{L.dlModal?.compatLabel || '系統相容性說明：'}</p>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, marginBottom: 12 }}>
              {L.dlModal?.compatIntro || 'MASQuant 交易相關功能目前主要支援 Windows 電腦，包含策略執行檔下載、MT5 連接、自動化交易設定與策略執行等功能。'}
            </p>
            <p style={{ ...TYPO.bodyM, color: COLORS.black, marginBottom: 8 }}>{L.dlModal?.compatNeedTitle || '以下功能需使用 Windows 電腦操作：'}</p>
            <ul style={{ paddingLeft: 18, margin: 0, marginBottom: 12 }}>
              {(L.dlModal?.compatNeed || ['下載策略交易執行檔', '連接 MT5 交易環境', '啟動或關閉自動化交易策略', '使用 MASQuant 相關本機端交易工具', '執行策略部署與交易操作']).map((t, i) => (
                <li key={i} style={{ ...TYPO.bodyM, color: COLORS.grayDark, lineHeight: '24px' }}>{t}</li>
              ))}
            </ul>
            <p style={{ ...TYPO.bodyM, color: COLORS.black, marginBottom: 8 }}>{L.dlModal?.compatUnsupTitle || '目前暫不支援：'}</p>
            <ul style={{ paddingLeft: 18, margin: 0, marginBottom: 12 }}>
              {(L.dlModal?.compatUnsup || ['macOS / Mac 電腦', 'iPhone / iPad', 'Android 手機 / 平板', 'Linux 系統']).map((t, i) => (
                <li key={i} style={{ ...TYPO.bodyM, color: COLORS.red, lineHeight: '24px' }}>{t}</li>
              ))}
            </ul>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, marginBottom: 12 }}>
              {L.dlModal?.compatNote || '如果您目前使用的是 Mac、手機或平板，仍可瀏覽 MASQuant 官網與部分網頁內容，但目前無法安裝與執行交易相關程式。'}
            </p>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, marginBottom: 12 }}>
              {L.dlModal?.compatAdvice || '為確保策略執行、MT5 連接與自動化交易功能正常運作，建議使用 Windows 電腦進行安裝、設定與操作；啟用方案或購買前，建議先確認是否有可使用的 Windows 電腦。'}
            </p>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark }}>
              {L.dlModal?.compatFuture || 'MASQuant 團隊將持續評估更多平台的支援可能，若未來支援 macOS 或其他系統，將另行公告。'}
            </p>
          </div>
          {/* 下載提醒 */}
          <div>
            <p style={{ ...TYPO.title, color: COLORS.black, marginBottom: 8 }}>{L.dlModal?.dlLabel || '下載提醒：'}</p>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, marginBottom: 12 }}>
              {L.dlModal?.dl1 || '若您在下載過程中遇到防毒軟體阻擋或誤刪檔案的情況，這通常是由於我們的安裝程式屬於自建工具，尚未被部分防毒資料庫熟悉所致。'}
            </p>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, marginBottom: 8 }}>{L.dlModal?.dl2 || '建議您可：'}</p>
            <ul style={{ paddingLeft: 18, margin: 0, marginBottom: 12 }}>
              <li style={{ ...TYPO.bodyM, color: COLORS.main, lineHeight: '24px' }}>{L.dlModal?.dlLi1 || '暫時停用防毒軟體後重新下載'}</li>
              <li style={{ ...TYPO.bodyM, color: COLORS.main, lineHeight: '24px' }}>{L.dlModal?.dlLi2 || '或將檔案加入信任清單'}</li>
            </ul>
            <p style={{ ...TYPO.bodyM, color: COLORS.grayDark }}>
              {L.dlModal?.dl3 || '我們的軟體已通過安全測試與簽章驗證，請您放心使用。如有任何疑問歡迎聯絡客服協助。'}
            </p>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: '24px 24px 0 24px', minHeight: 96, boxSizing: 'border-box', gap: 1.5, borderTop: '1px solid #E6E6E6', background: COLORS.white, alignItems: 'flex-start' }}>
          <button
            onClick={async () => {
              const target = downloadModalTarget;
              setDownloadModalTarget(null);
              if (target) await handleDownload(target);
            }}
            style={{
              display: 'flex', height: 48, padding: '12px 32px', boxSizing: 'border-box',
              justifyContent: 'center', alignItems: 'center', gap: 10,
              borderRadius: 8, border: 'none', background: COLORS.main,
              color: COLORS.white, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
              ...TYPO.title, cursor: 'pointer',
            }}
          >
            {L.dlModal?.startDownload || '開始下載'}
          </button>
        </DialogActions>
      </Dialog>

      {/* ───────────────────────── 下載中 loading 遮罩
          下載前伺服器要準備/傳輸檔案會停頓幾秒，用動畫 + 進度條讓使用者知道「沒卡住、正在處理」。
          有 Content-Length → 顯示實際百分比；沒有 → 不定量進度條 + 「準備中」文案。
          無 onClose / 無關閉鈕：下載完成（finally）才自動關，避免使用者誤關以為取消 */}
      <Dialog
        open={downloadingId !== null}
        PaperProps={{ sx: { borderRadius: 3, width: 360, maxWidth: '90vw', m: 2 } }}
      >
        <DialogContent sx={{ p: '32px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
          <img src="/images/Loading.gif" alt="" style={{ width: 96, height: 96, objectFit: 'contain' }} />
          <span style={{ ...TYPO.headingM, color: COLORS.black, textAlign: 'center' }}>
            {downloadPct == null
              ? (L.dl?.preparing || '正在準備您的下載檔案…')
              : fillTemplate(L.dl?.downloadingPct || '正在下載… {n}%', { n: downloadPct })}
          </span>
          <span style={{ ...TYPO.bodyM, color: COLORS.grayDark, textAlign: 'center', lineHeight: '21px' }}>
            {L.dl?.dlHint || '檔案較大時可能需要幾秒鐘，請勿關閉視窗'}
          </span>
          <LinearProgress
            variant={downloadPct == null ? 'indeterminate' : 'determinate'}
            value={downloadPct == null ? undefined : downloadPct}
            sx={{
              width: '100%', height: 8, borderRadius: 999, backgroundColor: '#F0F0F0',
              '& .MuiLinearProgress-bar': { backgroundColor: COLORS.main, borderRadius: 999 },
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ───────────────────────── 新手導引：如何開始交易
          首次從許願頁儲存策略後自動彈一次；之後可從頁首「使用教學」連結手動再開 */}
      <Dialog
        open={guideOpen}
        onClose={closeGuide}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, boxSizing: 'border-box', borderBottom: '1px solid #E6E6E6' }}>
          <span style={{ ...TYPO.modalTitle, color: COLORS.black }}>{L.guide?.title || '策略已儲存！接下來這樣做'}</span>
          <button
            onClick={closeGuide}
            style={{ display: 'flex', width: 40, height: 40, padding: 8, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <span style={{ display: 'flex', width: 24, height: 24, padding: 2, boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
              <img src="/images/icon/cross.png" alt={L.modal?.closeAlt || '關閉'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </span>
          </button>
        </DialogTitle>
        <DialogContent sx={{ p: '24px', pt: '24px !important', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, margin: 0 }}>
            {L.guide?.subtitle || '跟著這 4 個步驟，就能讓策略開始自動為您交易：'}
          </p>
          {[
            { t: L.guide?.s1Title || '下載策略', d: L.guide?.s1Desc || '點下方策略卡的「下載策略」，下載執行檔（.exe）。' },
            { t: L.guide?.s2Title || '開啟策略檔案', d: L.guide?.s2Desc || '在電腦上點兩下剛下載的策略 .exe 檔案。' },
            { t: L.guide?.s3Title || '登入交易程式', d: L.guide?.s3Desc || '依畫面指示，登入您的 MT5 交易程式。' },
            { t: L.guide?.s4Title || '開始交易', d: L.guide?.s4Desc || '完成後，策略就會開始自動為您交易。' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <span style={{
                flexShrink: 0, width: 32, height: 32, borderRadius: 999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: COLORS.lighter, color: COLORS.main,
                fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 16,
              }}>{i + 1}</span>
              <div style={{ flex: '1 1 0', minWidth: 0, paddingTop: 2 }}>
                <p style={{ ...TYPO.title, color: COLORS.black, margin: 0 }}>{s.t}</p>
                <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, margin: 0, marginTop: 2 }}>{s.d}</p>
              </div>
            </div>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: '20px 24px 24px 24px', boxSizing: 'border-box', borderTop: '1px solid #E6E6E6', background: COLORS.white, justifyContent: 'flex-end' }}>
          <button
            onClick={finishGuideModal}
            style={{
              display: 'flex', height: 48, padding: '12px 32px', boxSizing: 'border-box',
              justifyContent: 'center', alignItems: 'center', gap: 10,
              borderRadius: 8, border: 'none', background: COLORS.main,
              color: COLORS.white, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
              ...TYPO.title, cursor: 'pointer',
            }}
          >
            {L.guide?.gotIt || '我知道了，下載策略'}
          </button>
        </DialogActions>
      </Dialog>

      {/* ───────────────────────── 新手導引 STEP 2：聚光燈高亮第一張卡的「下載策略」
          按「我知道了」後出現：畫面變暗、只亮下載按鈕 + 氣泡指引；點外部 / 略過 / 下載即關閉 */}
      {spotlightOn && spotRect && (
        <>
          {/* 透明全螢幕點擊層：點外部關閉 */}
          <div onClick={closeSpotlight} style={{ position: 'fixed', inset: 0, zIndex: 1300, cursor: 'pointer' }} />
          {/* 聚光燈環：橘色高亮 + 周圍變暗（box-shadow 9999px）*/}
          <div style={{
            position: 'fixed',
            top: spotRect.top - 6, left: spotRect.left - 6,
            width: spotRect.width + 12, height: spotRect.height + 12,
            borderRadius: 12, boxSizing: 'border-box',
            border: '2px solid #F36D26',
            boxShadow: '0 0 0 4px rgba(243, 109, 38, 0.45), 0 0 0 9999px rgba(15, 23, 42, 0.55)',
            zIndex: 1301, pointerEvents: 'none',
          }} />
          {/* 氣泡：按鈕下方、置中對齊按鈕並夾在視窗內 */}
          <div style={{
            position: 'fixed',
            top: spotRect.top + spotRect.height + 16,
            left: Math.min(
              Math.max(12, spotRect.left + spotRect.width / 2 - 140),
              (typeof window !== 'undefined' ? window.innerWidth : 1280) - 292,
            ),
            width: 280, boxSizing: 'border-box',
            background: COLORS.white, borderRadius: 12, padding: 16,
            boxShadow: '0 16px 40px rgba(0,0,0,0.28)', zIndex: 1302,
          }}>
            <p style={{ ...TYPO.title, color: COLORS.black, margin: 0, marginBottom: 12 }}>
              {L.guide?.spotlight || '👇 點這裡下載您的第一個策略'}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={closeSpotlight}
                style={{ ...TYPO.bodyM, fontWeight: 500, color: COLORS.grayDark, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px' }}
              >
                {L.guide?.skip || '略過'}
              </button>
              <button
                onClick={() => { closeSpotlight(); if (strategies[0]) setDownloadModalTarget(strategies[0]); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 40, padding: '0 18px', borderRadius: 8, border: 'none',
                  background: COLORS.main, color: COLORS.white, ...TYPO.bodyM, fontWeight: 700, cursor: 'pointer',
                }}
              >
                <Download size={16} /> {L.dl?.download || '下載策略'}
              </button>
            </div>
          </div>
        </>
      )}

      <UpgradePlanDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
